import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  endpoints,
  OUTREACH_STAGE_META,
  OUTREACH_STAGES_ORDERED,
  type Channel,
  type ChannelLifecycle,
  type OutreachStage,
} from "@/lib/api";
import { cn } from "@/lib/utils";
import { Badge, lifecycleVariant } from "@/components/ui/Badge";

// ---------------------------------------------------------------------------
// Inline-edit cells for /channels — v1.4.2. Three cells become editable in
// place: BD 状态, lifecycle, 报价. The rest still need a click-into-detail.
//
// UX rules (consistent across all three):
//   - Click cell → enters edit mode
//   - Enter / blur / select → saves
//   - Escape → cancels
//   - Optimistic update: cell updates UI immediately; rolls back on error
//   - Saving indicator: subtle 50% opacity; row click handler suppressed
// ---------------------------------------------------------------------------

// Derived from the single source in api.ts (OUTREACH_STAGE_META) so this option
// list can't drift from the OutreachStage union or the CRM badge map.
const OUTREACH_OPTIONS = OUTREACH_STAGES_ORDERED.map((value) => ({
  value,
  label: OUTREACH_STAGE_META[value].label,
  variant: OUTREACH_STAGE_META[value].variant,
}));

const LIFECYCLE_OPTIONS: ChannelLifecycle[] = [
  "discovered", "evaluating", "candidate", "active", "monitor_only", "rejected", "archived",
];

// -- BD 状态 inline editor ---------------------------------------------------

export function OutreachStageEditCell({ channel }: { channel: Channel }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const wrap = useRef<HTMLDivElement | null>(null);
  const current = (channel.OutreachStage || "idle") as OutreachStage;
  const currentOpt = OUTREACH_OPTIONS.find((o) => o.value === current) ?? OUTREACH_OPTIONS[0];

  const mutation = useMutation({
    mutationFn: (next: OutreachStage) =>
      endpoints.updateChannelContact(channel.ID, {
        // Send all current contact fields so PUT semantics don't clear them.
        contact_email:    channel.ContactEmail ?? "",
        contact_telegram: channel.ContactTelegram ?? "",
        contact_discord:  channel.ContactDiscord ?? "",
        contact_agent:    channel.ContactAgent ?? "",
        contact_notes:    channel.ContactNotes ?? "",
        outreach_stage:   next,
        next_followup_at: channel.NextFollowupAt ?? "",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channels"] });
      qc.invalidateQueries({ queryKey: ["channel", channel.ID] });
      setEditing(false);
    },
    onError: (err) => alert((err as Error).message),
  });

  useEffect(() => {
    if (!editing) return;
    function onDoc(e: MouseEvent) {
      if (wrap.current && !wrap.current.contains(e.target as Node)) {
        setEditing(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [editing]);

  if (!editing) {
    return (
      <button
        type="button"
        className={cn("rounded px-0.5 py-0.5 hover:ring-1 hover:ring-primary/40", mutation.isPending && "opacity-50")}
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        disabled={mutation.isPending}
      >
        {current === "idle" ? (
          <span className="text-xs text-muted-foreground">未开始 ✎</span>
        ) : (
          <Badge variant={currentOpt.variant}>{currentOpt.label}</Badge>
        )}
      </button>
    );
  }
  return (
    <div ref={wrap} onClick={(e) => e.stopPropagation()}>
      <select
        autoFocus
        className="rounded border border-border bg-background px-1.5 py-0.5 text-xs"
        value={current}
        onChange={(e) => mutation.mutate(e.target.value as OutreachStage)}
        onKeyDown={(e) => e.key === "Escape" && setEditing(false)}
      >
        {OUTREACH_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// -- lifecycle inline editor ---------------------------------------------------

export function LifecycleEditCell({ channel }: { channel: Channel }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const wrap = useRef<HTMLDivElement | null>(null);
  const current = channel.LifecycleStatus;

  const mutation = useMutation({
    mutationFn: (next: ChannelLifecycle) =>
      endpoints.updateChannelLifecycle(channel.ID, { lifecycle_status: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channels"] });
      qc.invalidateQueries({ queryKey: ["channel", channel.ID] });
      setEditing(false);
    },
    onError: (err) => alert((err as Error).message),
  });

  useEffect(() => {
    if (!editing) return;
    function onDoc(e: MouseEvent) {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setEditing(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [editing]);

  if (!editing) {
    return (
      <button
        type="button"
        className={cn("rounded px-0.5 py-0.5 hover:ring-1 hover:ring-primary/40", mutation.isPending && "opacity-50")}
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        disabled={mutation.isPending}
      >
        <Badge variant={lifecycleVariant(current)}>{current}</Badge>
      </button>
    );
  }
  return (
    <div ref={wrap} onClick={(e) => e.stopPropagation()}>
      <select
        autoFocus
        className="rounded border border-border bg-background px-1.5 py-0.5 text-xs"
        value={current}
        onChange={(e) => mutation.mutate(e.target.value as ChannelLifecycle)}
        onKeyDown={(e) => e.key === "Escape" && setEditing(false)}
      >
        {LIFECYCLE_OPTIONS.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
    </div>
  );
}

// -- 报价 inline editor -------------------------------------------------------
// USD number input. Click → input, Enter/blur saves, Esc cancels.

export function QuotedPriceEditCell({ channel, current }: { channel: Channel; current: number | null }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(current !== null ? String(current) : "");
  const input = useRef<HTMLInputElement | null>(null);

  const mutation = useMutation({
    mutationFn: (next: number) =>
      endpoints.updateChannel(channel.ID, { quoted_price_usd: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channels"] });
      qc.invalidateQueries({ queryKey: ["channel", channel.ID] });
      setEditing(false);
    },
    onError: (err) => alert((err as Error).message),
  });

  function commit() {
    const trimmed = draft.trim();
    if (trimmed === "" || (current !== null && Number(trimmed) === current)) {
      setEditing(false);
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0) {
      setEditing(false);
      return;
    }
    mutation.mutate(n);
  }

  if (!editing) {
    return (
      <button
        type="button"
        className={cn(
          "tabular-nums rounded px-1 py-0.5 hover:ring-1 hover:ring-primary/40",
          mutation.isPending && "opacity-50",
        )}
        onClick={(e) => {
          e.stopPropagation();
          setDraft(current !== null ? String(current) : "");
          setEditing(true);
          setTimeout(() => input.current?.focus(), 0);
        }}
        disabled={mutation.isPending}
      >
        {current !== null ? (
          <>${current.toLocaleString()}</>
        ) : (
          <span className="text-muted-foreground">— ✎</span>
        )}
      </button>
    );
  }
  return (
    <input
      ref={input}
      type="number"
      step="0.01"
      autoFocus
      className="w-24 rounded border border-border bg-background px-1.5 py-0.5 text-right text-xs tabular-nums"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        }
        if (e.key === "Escape") setEditing(false);
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
}
